/**
 * Resolution of a validation finding's `path` onto a rendered field.
 *
 * `FieldState.path` is documented in the contract as the "stable dotted path of
 * the field within its section". `ValidationFinding.path` is a bare nullable
 * string with **no description at all**. The reasonable assumption — and it is
 * an assumption, raised on #16 — is that the two share the convention.
 *
 * Everything here therefore matches **exactly**. No prefix matching, no
 * case folding, no stripping of a presumed section prefix. Each of those would
 * turn a near-miss into a jump affordance that lands on the wrong control, and
 * a control that cannot do what it appears to do is exactly what the shell's
 * invariants forbid. A path that does not match is unresolvable, and an
 * unresolvable finding renders with no jump affordance at all.
 */

export function isResolvablePath(
  path: string | null | undefined,
  knownPaths: ReadonlySet<string>,
): path is string {
  return typeof path === "string" && path.length > 0 && knownPaths.has(path);
}

export function resolveFindingPath(
  path: string | null | undefined,
  knownPaths: ReadonlySet<string>,
): string | null {
  return isResolvablePath(path, knownPaths) ? path : null;
}

/**
 * DOM id for a field anchor.
 *
 * Derived rather than random so that server-rendered ids, `aria-describedby`
 * wiring, and the focus target all agree without threading a value through
 * every component. `encodeURIComponent` keeps a dotted path valid in an
 * attribute and reversible for debugging.
 */
export function fieldAnchorId(path: string): string {
  return `field-${encodeURIComponent(path)}`;
}
